import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { blogArticles } from '../content/blog/articles';
import { caseStudies } from '../content/blog/caseStudies';
import './BlogScreen.css';

const BlogScreen: React.FC = () => {
  const { theme } = useTheme();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'articles' | 'case-studies'>('articles');

  const featuredArticles = blogArticles.filter(article => article.featured);
  const regularArticles = blogArticles.filter(article => !article.featured);
  const featuredCaseStudies = caseStudies.filter(study => study.featured);
  const regularCaseStudies = caseStudies.filter(study => !study.featured);

  return (
    <div className="blog-screen" style={{ backgroundColor: theme.colors.background }}>
      <div className="container">
        <div className="blog-header">
          <h1 style={{ color: theme.colors.text }}>Blog & Resources</h1>
          <p className="blog-description" style={{ color: theme.colors.textSecondary }}>
            Explore articles, insights, and case studies to improve your game
          </p>
        </div>

        {isAdmin && (
          <div className="admin-access" style={{ 
            marginBottom: '2rem', 
            padding: '1rem', 
            background: theme.colors.surface,
            borderRadius: '8px',
            border: `2px solid ${theme.colors.primary}`
          }}>
            <h3 style={{ color: theme.colors.primary, marginBottom: '0.5rem' }}>
              🔒 Admin Access
            </h3>
            <Link 
              to="/blog/whitepaper" 
              style={{ 
                color: theme.colors.primary,
                textDecoration: 'underline',
                fontWeight: 'bold'
              }}
            >
              View White Paper →
            </Link>
          </div>
        )}

        <div className="blog-tabs">
          <button
            className={`blog-tab ${activeTab === 'articles' ? 'active' : ''}`}
            onClick={() => setActiveTab('articles')}
            style={{
              color: activeTab === 'articles' ? theme.colors.primary : theme.colors.textSecondary,
              borderBottomColor: activeTab === 'articles' ? theme.colors.primary : 'transparent'
            }}
          >
            Articles ({blogArticles.length})
          </button>
          <button
            className={`blog-tab ${activeTab === 'case-studies' ? 'active' : ''}`}
            onClick={() => setActiveTab('case-studies')}
            style={{
              color: activeTab === 'case-studies' ? theme.colors.primary : theme.colors.textSecondary,
              borderBottomColor: activeTab === 'case-studies' ? theme.colors.primary : 'transparent'
            }}
          >
            Case Studies ({caseStudies.length})
          </button>
        </div>

        {activeTab === 'articles' && (
          <div className="blog-content">
            {featuredArticles.length > 0 && (
              <section className="featured-section">
                <h2 style={{ color: theme.colors.text }}>Featured Articles</h2>
                <div className="articles-grid">
                  {featuredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/blog/article/${article.id}`}
                      className="article-card featured"
                      style={{
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.primary
                      }}
                    >
                      <div className="article-badge" style={{ backgroundColor: theme.colors.primary }}>
                        Featured
                      </div>
                      <h3 style={{ color: theme.colors.text }}>{article.title}</h3>
                      <p className="article-excerpt" style={{ color: theme.colors.textSecondary }}>
                        {article.excerpt}
                      </p>
                      <div className="article-meta">
                        <span style={{ color: theme.colors.textSecondary }}>
                          {article.readTime} min read
                        </span>
                        <span style={{ color: theme.colors.textSecondary }}>
                          {new Date(article.date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="article-tags">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="article-tag"
                            style={{
                              backgroundColor: theme.colors.primary + '20',
                              color: theme.colors.primary
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="articles-section">
              <h2 style={{ color: theme.colors.text }}>All Articles</h2>
              <div className="articles-grid">
                {regularArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/article/${article.id}`}
                    className="article-card"
                    style={{ backgroundColor: theme.colors.surface }}
                  >
                    <h3 style={{ color: theme.colors.text }}>{article.title}</h3>
                    <p className="article-excerpt" style={{ color: theme.colors.textSecondary }}>
                      {article.excerpt}
                    </p>
                    <div className="article-meta">
                      <span style={{ color: theme.colors.textSecondary }}>
                        {article.readTime} min read
                      </span>
                      <span style={{ color: theme.colors.textSecondary }}>
                        {new Date(article.date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="article-tags">
                      {article.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="article-tag"
                          style={{
                            backgroundColor: theme.colors.primary + '20',
                            color: theme.colors.primary
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        )}

        {activeTab === 'case-studies' && (
          <div className="blog-content">
            {featuredCaseStudies.length > 0 && (
              <section className="featured-section">
                <h2 style={{ color: theme.colors.text }}>Featured Case Studies</h2>
                <div className="case-studies-grid">
                  {featuredCaseStudies.map((study) => (
                    <Link
                      key={study.id}
                      to={`/blog/case-study/${study.id}`}
                      className="case-study-card featured"
                      style={{
                        backgroundColor: theme.colors.surface,
                        borderColor: theme.colors.primary
                      }}
                    >
                      <div className="case-study-badge" style={{ backgroundColor: theme.colors.primary }}>
                        Featured
                      </div>
                      <h3 style={{ color: theme.colors.text }}>{study.title}</h3>
                      <p className="case-study-excerpt" style={{ color: theme.colors.textSecondary }}>
                        {study.excerpt}
                      </p>
                      <div className="case-study-meta">
                        <span className="sport-badge" style={{ backgroundColor: theme.colors.primary + '20', color: theme.colors.primary }}>
                          {study.sport}
                        </span>
                        <span style={{ color: theme.colors.textSecondary }}>
                          {study.userLevel}
                        </span>
                        <span style={{ color: theme.colors.textSecondary }}>
                          {study.duration}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            <section className="case-studies-section">
              <h2 style={{ color: theme.colors.text }}>All Case Studies</h2>
              <div className="case-studies-grid">
                {regularCaseStudies.map((study) => (
                  <Link
                    key={study.id}
                    to={`/blog/case-study/${study.id}`}
                    className="case-study-card"
                    style={{ backgroundColor: theme.colors.surface }}
                  >
                    <h3 style={{ color: theme.colors.text }}>{study.title}</h3>
                    <p className="case-study-excerpt" style={{ color: theme.colors.textSecondary }}>
                      {study.excerpt}
                    </p>
                    <div className="case-study-meta">
                      <span className="sport-badge" style={{ backgroundColor: theme.colors.primary + '20', color: theme.colors.primary }}>
                        {study.sport}
                      </span>
                      <span style={{ color: theme.colors.textSecondary }}>
                        {study.userLevel}
                      </span>
                      <span style={{ color: theme.colors.textSecondary }}>
                        {study.duration}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogScreen;
